import { useState } from 'react';
import type { PrijsfavorietenPayload } from '../report';
import { fmtRange } from '../utils';

type Campaign = PrijsfavorietenPayload['campaign'];

/*
 * Hero: LIVE pill + campaign-day line, the five meta fields, and the campaign
 * key visual on the right (design's two-column `hero-with-visual` layout).
 *
 * The visual is served from /public rather than a bundled import so a missing
 * file can't break the build, and `onError` drops the whole column if it isn't
 * there yet — the hero then renders exactly as it did before, no broken-image
 * placeholder. Replace the file to update the artwork; no code change needed.
 *
 * `pf-hero-visual` overrides the shared AH landscape crop: this artwork is
 * portrait, so it is contained rather than cover-cropped (see the CSS).
 */
const VISUAL_SRC = '/prijsfavorieten-visual.png';

export function Hero({ campaign, previous }: { campaign: Campaign; previous?: Campaign }) {
  const [hasVisual, setHasVisual] = useState(true);

  return (
    <section className={`hero${hasVisual ? ' hero-with-visual' : ''}`}>
      <div className="hero-left">
        <div>
          <div className="hero-eyebrow">
            {campaign.live !== false && <span className="pill">LIVE</span>}
            {previous ? (
              <span>
                {campaign.periodLabel} ·{' '}
                {campaign.currentDay > 0
                  ? `Wk ${campaign.currentWeek} · Campagnedag ${campaign.currentDay} van ${campaign.totalDays}`
                  : `start ${campaign.start}`}
              </span>
            ) : (
              <span>
                Campagne dag {campaign.currentDay} van {campaign.totalDays} · Wk {campaign.currentWeek}
              </span>
            )}
          </div>
        </div>
        {previous ? (
          // Two parts: both windows side by side, and the set-up ("2 delen × 3 weken").
          <div className="hero-meta">
            <div className="hero-meta-row">
              <div>
                <span className="k">{previous.periodLabel} · Start – einde</span>
                <span className="v">{fmtRange(previous.start, previous.end)}</span>
              </div>
              <div>
                <span className="k">{campaign.periodLabel} · Start – einde</span>
                <span className="v">{fmtRange(campaign.start, campaign.end)}</span>
              </div>
              <div>
                <span className="k">Status</span>
                <span className="v">{campaign.status}</span>
              </div>
            </div>
            <div className="hero-meta-row">
              <div>
                <span className="k">Tempo</span>
                <span className="v">{campaign.pace}</span>
              </div>
              <div>
                <span className="k">Opzet</span>
                <span className="v">2 delen × {Math.round(campaign.totalDays / 7)} weken</span>
              </div>
            </div>
          </div>
        ) : (
          <div className="hero-meta">
            <div className="hero-meta-row">
              <div>
                <span className="k">Start</span>
                <span className="v">{campaign.start}</span>
              </div>
              <div>
                <span className="k">Einde</span>
                <span className="v">{campaign.end}</span>
              </div>
              <div>
                <span className="k">Status</span>
                <span className="v">{campaign.status}</span>
              </div>
            </div>
            <div className="hero-meta-row">
              <div>
                <span className="k">Tempo</span>
                <span className="v">{campaign.pace}</span>
              </div>
              <div>
                <span className="k">Weken</span>
                <span className="v">{campaign.weeksLabel}</span>
              </div>
            </div>
          </div>
        )}
      </div>
      {hasVisual && (
        <img className="hero-visual pf-hero-visual" src={VISUAL_SRC} alt={`Campagne visual — ${campaign.name}`} onError={() => setHasVisual(false)} />
      )}
    </section>
  );
}
